(() => {
  function safeParse(raw) {
    try { return JSON.parse(raw); } catch { return null; }
  }

  function findForm(details) {
    const formId = details.getAttribute("data-form-id");
    if (formId) {
      const f = document.getElementById(formId);
      if (f) return f;
    }
    return details.closest("section")?.querySelector("form[action*='/cart/add']")
      || details.closest("form[action*='/cart/add']");
  }

  function upsertProperty(form, label, value) {
    const cleanLabel = String(label || "").trim().replace(/:\s*$/, "");
    const name = `properties[${cleanLabel}]`;
    let input = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function esc(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  function buildOptionIndexMap(product) {
    const map = new Map();
    (product.options || []).forEach((name, i) => {
      map.set(String(name || "").trim().toLowerCase(), i + 1); // 1-based
    });
    return map;
  }

  /**
   * IMPORTANT: We DO NOT strip single trailing/leading quotes
   * because variant values may contain inch symbols like 24" x 24".
   *
   * We only remove WRAPPING quotes when both ends match.
   */
  function cleanMetafieldValue(v) {
  let s = String(v ?? "").trim();
  if (!s) return "";

  const isQuote = (ch) =>
    ch === '"' || ch === "'" || ch === "“" || ch === "”" || ch === "‘" || ch === "’";

  // If the entire token is only a quote, remove it.
  if (s.length === 1 && isQuote(s)) return "";

  // Remove wrapping quotes if both ends are quotes.
  if (s.length >= 2 && isQuote(s[0]) && isQuote(s[s.length - 1])) {
    s = s.slice(1, -1).trim();
  }

  // Remove a leading quote (common when metafield values look like: "Value)
  if (s && isQuote(s[0])) {
    s = s.slice(1).trimStart();
  }

  // Remove a trailing quote ONLY if it is not an inch mark (i.e., not preceded by a digit).
  // This keeps things like 24" intact IF it ever appears in metafields.
  while (
    s.length >= 2 &&
    isQuote(s[s.length - 1]) &&
    !/\d/.test(s[s.length - 2])
  ) {
    s = s.slice(0, -1).trimEnd();
  }

  // Re-check for leftover standalone quote
  if (s.length === 1 && isQuote(s)) return "";

  return s;
}


  function normalizeMetafieldRowValues(rows) {
    rows.forEach((r) => {
      // Only normalize rows that are NOT variant-driven (metafields)
      if (r && r.variant) return;

      if (!Array.isArray(r.values)) return;

      // Swatches can be metaobject payload objects; clean label only
      if (String(r.ui || "").toLowerCase() === "swatches") {
        r.values = r.values
          .map((v) => {
            if (v && typeof v === "object") {
              const lbl = cleanMetafieldValue(v.label);
              return { ...v, label: lbl };
            }
            return cleanMetafieldValue(v);
          })
          .filter((v) => {
            if (v && typeof v === "object") return !!String(v.label || "").trim();
            return String(v || "").trim() !== "";
          });
        return;
      }

      // Regular metafield pill rows
      r.values = r.values
        .map(cleanMetafieldValue)
        .filter((x) => String(x || "").trim() !== "");
    });
  }

  function setVariantBySelectedOptions(form, product, selectedByOptionName) {
    const variants = product.variants || [];
    if (!variants.length) return null;

    const optionIndexMap = buildOptionIndexMap(product);

    const match = variants.find(v => {
      for (const [optName, optValue] of selectedByOptionName.entries()) {
        const idx = optionIndexMap.get(String(optName || "").trim().toLowerCase());
        if (!idx) continue;
        if (String(v[`option${idx}`] || "") !== String(optValue || "")) return false;
      }
      return true;
    }) || variants.find(v => v.available) || variants[0];

    if (!match) return null;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = match.id;
      idInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    // Let theme listeners respond (covers most themes)
    try {
      form.dispatchEvent(new CustomEvent("variant-change", { bubbles: true, detail: { variant: match } }));
      document.dispatchEvent(new CustomEvent("variant-change", { detail: { variant: match } }));
      form.dispatchEvent(new CustomEvent("variant:change", { bubbles: true, detail: { variant: match } }));
      document.dispatchEvent(new CustomEvent("variant:change", { detail: { variant: match } }));
    } catch (_) {}

    return match;
  }

  function init(details) {
    if (details.__czInit) return;
    details.__czInit = true;

    const mount = details.querySelector("[data-cz-render]");
    const errorBox = details.querySelector(".cz__error");
    const form = findForm(details);
    if (!mount || !form) return;

    const product = safeParse(details.querySelector('script[data-cz-product]')?.textContent || "");
    const rowsPayload = safeParse(details.querySelector('script[data-cz-rows]')?.textContent || "");
    if (!product || !rowsPayload) return;

    const requireAll = details.getAttribute("data-require-all") === "true";
    const errorMsg = details.getAttribute("data-error-message") || "Please choose all required options.";

    const sizeOptionName = details.getAttribute("data-size-option-name") || "";
    const thicknessOptionName = details.getAttribute("data-thickness-option-name") || "";
    const rushOptionName = details.getAttribute("data-rush-option-name") || "";

    const rows = (rowsPayload.rows || [])
      .filter(r => Array.isArray(r.values) && r.values.length > 0);

    // Clean ONLY metafield rows (fixes the stray quote pills) — does NOT touch variants
    normalizeMetafieldRowValues(rows);

    // Identify color row
    const colorRow = rows.find(r =>
      String(r.key || "").trim().toLowerCase() === "color" ||
      String(r.ui || "").trim().toLowerCase() === "swatches"
    );

    // Partition rows
    const singlesTop = [];
    const g1 = [];
    const g2 = [];
    const singlesBottom = [];

    rows.forEach(r => {
      if (colorRow && r.key === colorRow.key) return;

      if (r.group === "g1") g1.push(r);
      else if (r.group === "g2") g2.push(r);
      else {
        if (r.key === "absorbs_to") singlesTop.push(r);
        else singlesBottom.push(r);
      }
    });

    const renderUnits = [];
    singlesTop.forEach(r => renderUnits.push({ type: "section", ...r }));
    if (g1.length) renderUnits.push({ type: "group", group: "g1", sections: g1 });
    if (colorRow) renderUnits.push({ type: "color", ...colorRow });
    if (g2.length) renderUnits.push({ type: "group", group: "g2", sections: g2 });
    singlesBottom.forEach(r => renderUnits.push({ type: "section", ...r }));

    // Render
    const html = [];
    renderUnits.forEach((unit, idx) => {
      if (idx > 0) html.push(`<div class="cz__divider"></div>`);

      if (unit.type === "group") {
        unit.sections.forEach(s => html.push(sectionHTML(s)));
      } else if (unit.type === "color") {
        html.push(colorHTML(unit));
      } else {
        html.push(sectionHTML(unit));
      }
    });
    mount.innerHTML = html.join("");

    // Selection state
    const selected = {};
    const selectedVariantOptions = new Map();

    const currentVariant =
      product.selected_or_first_available_variant ||
      product.variants?.find(v => v.available) ||
      product.variants?.[0];

    const optionIndexMap = buildOptionIndexMap(product);

    function getVariantOptValue(optName) {
      const idx = optionIndexMap.get(String(optName || "").trim().toLowerCase());
      return (currentVariant && idx) ? currentVariant[`option${idx}`] : null;
    }

    // Defaults for non-variant rows (properties)
    rows.forEach(r => {
      if (colorRow && r.key === colorRow.key) return;
      if (r.variant) return;

      const first = r.values[0];
      selected[r.key] = first;

      setCurrent(mount, r.key, first);
      setActive(mount, r.key, first);

      upsertProperty(form, r.label, first);
    });

    // Variant rows default from current variant
    const variantRows = rows.filter(r => r.variant);
    variantRows.forEach(r => {
      let optName = "";
      if (r.key === "size") optName = sizeOptionName;
      if (r.key === "thickness") optName = thicknessOptionName;
      if (r.key === "rush_production") optName = rushOptionName;

      const fromVariant = getVariantOptValue(optName);
      const fallback = r.values[0];

      // IMPORTANT: no cleaning here (keeps inch symbols intact)
      const val = (fromVariant && r.values.includes(fromVariant)) ? fromVariant : fallback;

      selected[r.key] = val;
      setCurrent(mount, r.key, val);
      setActive(mount, r.key, val);

      if (optName) selectedVariantOptions.set(optName, val);
    });

    // Apply initial variant
    if (selectedVariantOptions.size) {
      setVariantBySelectedOptions(form, product, selectedVariantOptions);
    }

    // Color row default (property only)
    if (colorRow) {
      const first = colorRow.values[0];
      const val = (typeof first === "object" && first) ? (first.label || "") : String(first || "");
      selected[colorRow.key] = val;

      setCurrent(mount, colorRow.key, val);
      setActive(mount, colorRow.key, val);

      upsertProperty(form, colorRow.label, val);
    }

    function validate() {
      if (!requireAll) return true;
      const missing = rows.some(r => !selected[r.key]);
      if (missing) {
        if (errorBox) { errorBox.textContent = errorMsg; errorBox.hidden = false; }
        details.open = true;
        return false;
      }
      if (errorBox) errorBox.hidden = true;
      return true;
    }

    // Pill change
    details.addEventListener("change", (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== "radio") return;

      const key = input.getAttribute("data-key");
      const label = input.getAttribute("data-label");
      const isVariant = input.getAttribute("data-variant") === "true";
      const optName = input.getAttribute("data-option-name") || "";
      if (!key || !label) return;

      // IMPORTANT:
      // - Variant values: use raw input.value (keeps inch symbols)
      // - Metafield values: can be cleaned (but by now they already are)
      const val = input.value;

      selected[key] = val;
      setCurrent(mount, key, val);
      setActive(mount, key, val);

      if (!isVariant) {
        upsertProperty(form, label, val);
      } else {
        if (optName) selectedVariantOptions.set(optName, val);
        setVariantBySelectedOptions(form, product, selectedVariantOptions);
      }

      if (errorBox) errorBox.hidden = true;
    });

    // Swatch click
    details.addEventListener("click", (e) => {
      const sw = e.target.closest(".cz__swatch");
      if (sw) {
        const key = sw.getAttribute("data-key");
        const label = sw.getAttribute("data-label");
        const val = sw.getAttribute("data-value");
        if (!key || !val) return;

        selected[key] = val;
        setCurrent(mount, key, val);
        setActive(mount, key, val);

        if (label) upsertProperty(form, label, val);

        if (errorBox) errorBox.hidden = true;
        return;
      }

      // Tooltip click
      const infoBtn = e.target.closest("[data-tooltip]");
      if (infoBtn) {
        e.preventDefault();
        e.stopPropagation();
        const text = infoBtn.getAttribute("data-tooltip");
        if (!text) return;

        document.querySelectorAll(".cz__tip").forEach(x => x.remove());
        const tip = document.createElement("div");
        tip.className = "cz__tip";
        tip.textContent = text;
        document.body.appendChild(tip);

        const r = infoBtn.getBoundingClientRect();
        tip.style.top = `${window.scrollY + r.bottom + 8}px`;
        tip.style.left = `${Math.min(window.scrollX + r.left, window.scrollX + document.documentElement.clientWidth - 320)}px`;
      }
    });

    document.addEventListener("click", (e) => {
      if (e.target.closest(".cz__tip")) return;
      if (e.target.closest("[data-tooltip]")) return;
      document.querySelectorAll(".cz__tip").forEach(x => x.remove());
    });

    form.addEventListener("submit", (e) => {
      if (!validate()) e.preventDefault();
    });

    function sectionHTML(s) {
      const key = s.key;
      const label = s.label || "Option";
      const info = s.info || "";
      const values = Array.isArray(s.values) ? s.values : [];
      const note = s.note || "";
      const isVariant = !!s.variant;

      let optName = "";
      if (key === "size") optName = sizeOptionName;
      if (key === "thickness") optName = thicknessOptionName;
      if (key === "rush_production") optName = rushOptionName;

      const infoBtn = info ? `<button class="cz__info" type="button" data-tooltip="${esc(info)}">i</button>` : "";

      const pills = values.map((v, i) => `
        <label class="cz__pill ${i === 0 ? "is-active" : ""}">
          <input type="radio" ${i === 0 ? "checked" : ""}
            name="cz-${esc(key)}"
            value="${esc(v)}"
            data-key="${esc(key)}"
            data-label="${esc(label)}"
            data-variant="${isVariant ? "true" : "false"}"
            data-option-name="${esc(optName)}"
          >
          <span>${esc(v)}</span>
        </label>
      `).join("");

      const noteHtml = note ? `<div class="cz__note">${esc(note)}</div>` : "";

      return `
        <div class="cz__row" data-section="${esc(key)}" data-ui="pills">
          <div class="cz__label-row">
            <span class="cz__label">${esc(label)}</span>
            <span class="cz__current" data-current="${esc(key)}"></span>
            ${infoBtn}
          </div>
          <div class="cz__values">${pills}</div>
          ${noteHtml}
        </div>
      `;
    }

    function colorHTML(unit) {
      const key = unit.key || "color";
      const label = unit.label || "Color:";
      const info = unit.info || "";
      const values = Array.isArray(unit.values) ? unit.values : [];

      const infoBtn = info ? `<button class="cz__info" type="button" data-tooltip="${esc(info)}">i</button>` : "";

      const swatches = values.map((v, i) => {
        const obj = (typeof v === "object" && v) ? v : { label: String(v || ""), hex: "", img: "" };
        const val = obj.label || "";

        let styleParts = [];
        if (obj.hex) styleParts.push(`--swatch:${obj.hex}`);
        if (obj.img) styleParts.push(`--swatch-img:url('${obj.img}')`);
        const styleAttr = styleParts.length ? `style="${esc(styleParts.join(";"))}"` : "";

        return `<button
          type="button"
          class="cz__swatch ${i === 0 ? "is-active" : ""}"
          data-key="${esc(key)}"
          data-label="${esc(label)}"
          data-value="${esc(val)}"
          aria-label="${esc(val)}"
          ${styleAttr}
        ></button>`;
      }).join("");

      return `
        <div class="cz__row" data-section="${esc(key)}" data-ui="swatches">
          <div class="cz__label-row">
            <span class="cz__label">${esc(label)}</span>
            <span class="cz__current" data-current="${esc(key)}"></span>
            ${infoBtn}
          </div>
          <div class="cz__swatches">${swatches}</div>
        </div>
      `;
    }

    function setCurrent(root, key, val) {
      const el = root.querySelector(`[data-current="${CSS.escape(key)}"]`);
      if (el) el.textContent = val || "";
    }

    function setActive(root, key, val) {
      const section = root.querySelector(`[data-section="${CSS.escape(key)}"]`);
      if (!section) return;

      const ui = section.getAttribute("data-ui");
      if (ui === "swatches") {
        section.querySelectorAll(".cz__swatch").forEach(btn => {
          btn.classList.toggle("is-active", btn.getAttribute("data-value") === val);
        });
      } else {
        section.querySelectorAll(".cz__pill").forEach(p => p.classList.remove("is-active"));
        const input = section.querySelector(`input[type="radio"][value="${CSS.escape(val)}"]`);
        if (input) input.closest(".cz__pill")?.classList.add("is-active");
      }
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll("details[data-cz]").forEach(init);
  }

  function boot(scope = document) {
    initAll(scope);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", (e) => boot(e.target));
  document.addEventListener("shopify:section:select", (e) => boot(e.target));
  document.addEventListener("shopify:block:select", (e) => boot(e.target));
  document.addEventListener("shopify:block:deselect", (e) => boot(e.target));
})();
